import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification
} from "firebase/auth";
import { ref, set } from "firebase/database";
import { useNavigate, Link } from "react-router-dom";
import { 
  FaUser, FaEnvelope, FaKey, FaEye, FaEyeSlash, 
  FaMoon, FaSun, FaCheckCircle, FaGlobe, FaShieldAlt
} from "react-icons/fa";
import "./Auth.css";

function Register() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneCountry: "+91",
    phone: "",
    password: "",
    confirmPassword: "",
    agreeTerms: false
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // Theme Management
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "dark");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Password Strength Calculation
  const getPasswordStrength = (pwd) => {
    if (!pwd) return { score: 0, text: "", color: "transparent" };
    let score = 0;
    if (pwd.length >= 6) score += 1;
    if (pwd.length >= 10) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;

    if (score <= 2) return { score: 33, text: "Weak", color: "#ef4444" };
    if (score <= 4) return { score: 66, text: "Medium", color: "#eab308" };
    return { score: 100, text: "Strong", color: "#00a884" };
  };

  const pwdStrength = getPasswordStrength(formData.password);

  const registerUser = async (e) => {
    e.preventDefault();
    setError("");
    setInfoMessage("");

    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
      setError("Please fill in all required fields.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    if (!formData.agreeTerms) {
      setError("You must agree to the Terms & Privacy Policy.");
      return;
    }

    try {
      setLoading(true);

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const user = userCredential.user;

      // Send Email Verification
      await sendEmailVerification(user, {
        url: window.location.origin + "/chat",
        handleCodeInApp: false
      });

      // Full Phone Number with prefix
      const fullPhone = formData.phone ? `${formData.phoneCountry}${formData.phone}` : "";

      // Save user entry in Realtime Database
      await set(ref(db, "users/" + user.uid), {
        uid: user.uid,
        name: formData.name,
        email: formData.email,
        phone: fullPhone,
        photoURL: "",
        bio: "Available on Bharat Connect",
        isEmailVerified: false,
        online: false,
        createdAt: Date.now()
      });

      setInfoMessage(
        "Account created successfully! Verification email sent. Please check your inbox before logging in."
      );

      setTimeout(() => {
        navigate("/login");
      }, 3500);

    } catch (err) {
      console.error(err);
      switch (err.code) {
        case "auth/email-already-in-use":
          setError("This email address is already in use.");
          break;
        case "auth/invalid-email":
          setError("Invalid email format.");
          break;
        case "auth/weak-password":
          setError("Password is too weak.");
          break;
        default:
          setError(err.message || "Failed to create account.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-container">
      {/* Top Navbar */}
      <header className="auth-navbar">
        <Link to="/" className="auth-nav-logo">
          <div className="auth-logo-icon">
            <FaShieldAlt />
          </div>
          <span>Bharat Connect</span>
        </Link>
        <button className="auth-theme-toggle" onClick={toggleTheme}>
          {theme === "dark" ? <FaSun style={{ color: '#eab308' }} /> : <FaMoon style={{ color: '#00a884' }} />}
          <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
        </button>
      </header>

      {/* Main Split Layout (45% Branding / 55% Glass Card) */}
      <main className="auth-main-content">
        {/* 45% Branding Panel */}
        <section className="auth-brand-panel">
          <div>
            <div className="auth-brand-badge">
              <FaGlobe /> <span>Join Bharat Connect</span>
            </div>
            <h1 className="auth-brand-title">Secure Messaging Platform</h1>
            <p className="auth-brand-subtitle">
              Connect seamlessly with individuals & teams across India.
            </p>
            
            <div className="auth-brand-features">
              <div className="auth-feature-item">
                <div className="auth-feature-icon">
                  <FaCheckCircle />
                </div>
                <span>Private & One-on-One Chats</span>
              </div>
              <div className="auth-feature-item">
                <div className="auth-feature-icon">
                  <FaCheckCircle />
                </div>
                <span>Group Messaging & Communities</span>
              </div>
              <div className="auth-feature-item">
                <div className="auth-feature-icon">
                  <FaCheckCircle />
                </div>
                <span>Instant Notifications</span>
              </div>
              <div className="auth-feature-item">
                <div className="auth-feature-icon">
                  <FaCheckCircle />
                </div>
                <span>High Quality File Sharing</span>
              </div>
              <div className="auth-feature-item">
                <div className="auth-feature-icon">
                  <FaCheckCircle />
                </div>
                <span>Encrypted Communication</span>
              </div>
            </div>
          </div>

          <div className="auth-brand-footer">
            © 2026 Bharat Connect. Building the future of private communication.
          </div>
        </section>

        {/* 55% Glass Auth Card */}
        <section className="auth-form-card">
          <div className="auth-card-header">
            <h2>Create Account 🚀</h2>
            <p>Start secure conversations in minutes.</p>
          </div>

          {error && <div className="auth-error-alert">{error}</div>}
          {infoMessage && <div className="auth-success-alert">{infoMessage}</div>}

          <form onSubmit={registerUser}>
            {/* Full Name */}
            <div className="auth-form-group">
              <label>Full Name *</label>
              <div className="auth-input-wrapper">
                <FaUser className="auth-input-icon" />
                <input
                  type="text"
                  name="name"
                  className="auth-input"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* Email Address */}
            <div className="auth-form-group">
              <label>Email Address *</label>
              <div className="auth-input-wrapper">
                <FaEnvelope className="auth-input-icon" />
                <input
                  type="email"
                  name="email"
                  className="auth-input"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* Phone Number */}
            <div className="auth-form-group">
              <label>Phone Number (Optional)</label>
              <div className="auth-phone-wrapper">
                <select
                  name="phoneCountry"
                  className="auth-phone-prefix"
                  value={formData.phoneCountry}
                  onChange={handleChange}
                >
                  <option value="+91">+91 🇮🇳</option>
                </select>
                <input
                  type="tel"
                  name="phone"
                  className="auth-phone-input"
                  placeholder="9876543210"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Password */}
            <div className="auth-form-group">
              <label>Password *</label>
              <div className="auth-input-wrapper">
                <FaKey className="auth-input-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  className="auth-input"
                  placeholder="••••••••••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <div
                  className="auth-eye-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </div>
              </div>
              {formData.password && (
                <div className="password-strength-container">
                  <div className="strength-bar-track">
                    <div 
                      className="strength-bar-fill" 
                      style={{ width: `${pwdStrength.score}%`, backgroundColor: pwdStrength.color }}
                    />
                  </div>
                  <span className="strength-text" style={{ color: pwdStrength.color }}>
                    Strength: {pwdStrength.text}
                  </span>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="auth-form-group">
              <label>Confirm Password *</label>
              <div className="auth-input-wrapper">
                <FaKey className="auth-input-icon" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  className="auth-input"
                  placeholder="••••••••••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
                <div
                  className="auth-eye-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </div>
              </div>
              {formData.confirmPassword && (
                <div style={{ marginTop: '4px', fontSize: '12px', fontWeight: '600', color: formData.password === formData.confirmPassword ? '#00a884' : '#ef4444' }}>
                  {formData.password === formData.confirmPassword ? "✓ Passwords match" : "✗ Passwords do not match"}
                </div>
              )}
            </div>

            {/* Terms Checkbox */}
            <div className="auth-extras-row" style={{ marginBottom: "20px" }}>
              <label className="auth-checkbox-label">
                <input
                  type="checkbox"
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={handleChange}
                  required
                />
                <span>
                  I agree to the <Link to="/terms" style={{ color: '#00a884', fontWeight: '600' }}>Terms</Link> & <Link to="/privacy" style={{ color: '#00a884', fontWeight: '600' }}>Privacy Policy</Link>
                </span>
              </label>
            </div>

            <button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading ? "Creating Account..." : "CREATE ACCOUNT"}
            </button>
          </form>

          <div className="auth-card-footer" style={{ marginTop: "24px" }}>
            <span>Already have an account?</span>
            <Link to="/login">Login →</Link>
          </div>
        </section>
      </main>
    </div>
  );
}

export default Register;
